import React from 'react';
import { Paper, Box, Typography, Icon, useTheme, alpha } from '@mui/material';

interface StatCardProps {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    trend?: {
        value: string;
        isPositive: boolean;
    };
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, trend }) => {
    const theme = useTheme();
    
    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 5,
                border: '1px solid',
                borderColor: alpha(color, 0.1),
                position: 'relative',
                overflow: 'hidden',
                background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
                backdropFilter: 'blur(10px)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: `0 20px 40px ${alpha(color, 0.15)}`,
                    borderColor: alpha(color, 0.3),
                    '& .icon-container': {
                        transform: 'scale(1.1) rotate(5deg)',
                        bgcolor: color,
                        color: '#fff',
                    }
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(color, 0.1)} 0%, transparent 70%)`,
                    zIndex: 0,
                }
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ position: 'relative', zIndex: 1 }}>
                <Box>
                    <Typography 
                        variant="overline" 
                        color="text.secondary" 
                        fontWeight="800" 
                        sx={{ letterSpacing: 1.5, opacity: 0.8, textTransform: 'uppercase', fontSize: '0.65rem' }}
                    >
                        {title}
                    </Typography>
                    <Typography variant="h3" fontWeight="900" sx={{ mt: 0.5, letterSpacing: -1 }}>
                        {value}
                    </Typography>
                    {trend && (
                        <Box display="flex" alignItems="center" sx={{ mt: 2 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    bgcolor: trend.isPositive ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                                    color: trend.isPositive ? 'success.main' : 'error.main',
                                    px: 1.2,
                                    py: 0.4,
                                    borderRadius: 2,
                                    mr: 1.5
                                }}
                            >
                                <Typography variant="caption" fontWeight="800">
                                    {trend.isPositive ? '↑' : '↓'} {trend.value}
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" fontWeight="600">
                                from last week
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Box
                    className="icon-container"
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 3.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(color, 0.1),
                        color: color,
                        transition: 'all 0.3s ease',
                        boxShadow: `0 8px 16px ${alpha(color, 0.1)}`,
                    }}
                >
                    {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 28 } })}
                </Box>
            </Box>
        </Paper>
    );
};

export default StatCard;
